import { useState, useCallback, useRef } from 'react';
import { reorderDocChildren } from '../../../services/api';

/**
 * Builds a unique sortable id from an item's type + id to avoid collisions
 * between directories and pages that could share numeric ids.
 */
export function sortableId(item) {
    return `${item.type}-${item.id}`;
}

export function parseSortableId(sid) {
    const m = String(sid).match(/^(directory|page)-(\d+)$/);
    if (!m) return null;
    return { type: m[1], id: parseInt(m[2], 10) };
}

/**
 * Convert the flat childrenCacheRef (Map<parentId, items[]>) into a nested
 * TreeItems structure compatible with dnd-kit-sortable-tree.
 *
 * Each node has: { id (sortableId string), children, collapsed, canHaveChildren, ...original item data }
 */
export function buildTreeItems(childrenCache, rootItems, expandedDirs, canDrag) {
    const buildNode = (item) => {
        const isDir = item.type === 'directory';
        const children = isDir ? (childrenCache.get(item.id) || []) : [];
        const isExpanded = expandedDirs.has(item.id);
        return {
            id: sortableId(item),
            _item: item,
            children: children.length > 0 ? children.map(buildNode) : [],
            collapsed: isDir && !isExpanded,
            canHaveChildren: isDir,
            disableSorting: !canDrag || !!item.archived,
        };
    };
    return rootItems.map(buildNode);
}

/**
 * Sync the tree back to the childrenCacheRef after a DnD operation.
 * Walks the tree and updates the cache for each parent.
 */
function syncTreeToCache(treeItems, parentId, childrenCacheRef) {
    const children = treeItems.map((node) => node._item);
    childrenCacheRef.current.set(parentId, children);
    for (const node of treeItems) {
        if (node.children && node.children.length > 0) {
            syncTreeToCache(node.children, node._item.id, childrenCacheRef);
        }
    }
}

/**
 * Extract ordered sibling items from a tree level for the reorder API.
 */
function extractReorderPayload(treeItems) {
    return treeItems.map((node, i) => ({
        id: node._item.id,
        type: node._item.type,
        weight: i,
    }));
}

/**
 * Hook managing drag-and-drop logic for the doc sidebar tree.
 *
 * Works with dnd-kit-sortable-tree's SortableTree component.
 * Detects reorder (same parent) vs move (cross-parent) from the
 * `reason` callback argument and calls the appropriate API.
 *
 * @param {React.MutableRefObject<Map>} childrenCacheRef
 * @param {Function} setChildrenVersion
 * @param {boolean} canDrag
 * @param {number|null} currentSpaceId
 * @param {Function} onMoveDetected - called with { item, fromParentId, toParentId } for cross-parent moves
 */
export default function useDocTreeDnd(childrenCacheRef, setChildrenVersion, canDrag, currentSpaceId, onMoveDetected) {
    const snapshotRef = useRef(null);
    const [savingReorder, setSavingReorder] = useState(false);

    const handleItemsChanged = useCallback((newItems, reason) => {
        if (!canDrag) return;

        if (reason.type === 'collapsed' || reason.type === 'expanded') {
            return;
        }

        if (reason.type === 'dropped') {
            const draggedItem = reason.draggedItem;
            const fromParent = reason.draggedFromParent;
            const toParent = reason.droppedToParent;

            const fromParentId = fromParent ? parseSortableId(fromParent.id) : null;
            const toParentId = toParent ? parseSortableId(toParent.id) : null;

            const fromId = fromParentId?.id ?? currentSpaceId;
            const toId = toParentId?.id ?? currentSpaceId;

            const parsed = parseSortableId(draggedItem.id);
            if (!parsed) return;

            const isSameParent = fromId === toId;

            snapshotRef.current = new Map();
            for (const [k, v] of childrenCacheRef.current) {
                snapshotRef.current.set(k, [...v]);
            }

            syncTreeToCache(newItems, currentSpaceId, childrenCacheRef);
            setChildrenVersion((v) => v + 1);

            if (isSameParent) {
                const findSiblings = (nodes, targetParentSid) => {
                    if (!targetParentSid || (currentSpaceId && toId === currentSpaceId)) {
                        return nodes;
                    }
                    for (const node of nodes) {
                        if (node.id === toParent?.id) return node.children || [];
                        if (node.children?.length) {
                            const found = findSiblings(node.children, targetParentSid);
                            if (found) return found;
                        }
                    }
                    return null;
                };

                const siblings = toParent ? findSiblings(newItems, toParent.id) : newItems;
                if (!siblings) return;

                const payload = extractReorderPayload(siblings);
                setSavingReorder(true);
                reorderDocChildren(toId, payload)
                    .then((data) => {
                        if (data?.status === 'partial' && data?.errors?.length) {
                            console.warn('[DocTree] Partial reorder:', data.errors);
                        }
                    })
                    .catch((err) => {
                        console.error('[DocTree] Reorder failed:', err.message);
                        if (snapshotRef.current) {
                            for (const [k, v] of snapshotRef.current) {
                                childrenCacheRef.current.set(k, v);
                            }
                            setChildrenVersion((v) => v + 1);
                        }
                    })
                    .finally(() => {
                        snapshotRef.current = null;
                        setSavingReorder(false);
                    });
            } else {
                if (snapshotRef.current) {
                    for (const [k, v] of snapshotRef.current) {
                        childrenCacheRef.current.set(k, v);
                    }
                    setChildrenVersion((v) => v + 1);
                    snapshotRef.current = null;
                }

                onMoveDetected?.({
                    item: parsed,
                    fromParentId: fromId,
                    toParentId: toId,
                });
            }
        }
    }, [canDrag, childrenCacheRef, setChildrenVersion, currentSpaceId, onMoveDetected]);

    return {
        handleItemsChanged,
        savingReorder,
    };
}
